import { Context, Controller } from '@hotwired/stimulus';

type InputsMap = Omit<
  Record<string, (HTMLInputElement | HTMLTextAreaElement)[]>,
  '__order'
> & {
  __order: string[];
};

/**
 * Enables special actions for checklist forms
 */
export default class ChecklistsFormController extends Controller {
  private form!: HTMLFormElement;
  private inputs!: InputsMap;

  constructor(context: Context) {
    super(context);
  }

  public connect(): void {
    this.form = this.element.closest('form')!;

    const elements = this.form.querySelectorAll<
      HTMLInputElement | HTMLTextAreaElement
    >('input[name], textarea[name]');

    this.inputs = Array.from(elements)
      .filter(
        (input) =>
          input.type !== 'hidden' &&
          input.type !== 'submit' &&
          !input.hasAttribute('data-trix-input'),
      )
      .reduce<InputsMap>(
        (groups, input) => {
          const name = input.getAttribute('name');
          if (!name) {
            return groups;
          }

          if (!groups[name]) {
            groups.__order.push(name);
            groups[name] = [];
          }

          groups[name].push(input);

          return groups;
        },
        { __order: [] },
      );

    this.form
      .querySelectorAll<HTMLAnchorElement>(
        'a[href]:not([href=""]):not([target="_self"])',
      )
      .forEach((anchor) => {
        if (anchor.closest('[contenteditable="true"]')) {
          return;
        }

        anchor.setAttribute('data-original-target', anchor.target);
        anchor.setAttribute('target', '_blank');

        const indicator = document.createElement('span');
        indicator.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" class="size-3 ml-1 inline-block align-middle" width="12" height="12">
          <path d="M6.22 8.72a.75.75 0 0 0 1.06 1.06l5.22-5.22v1.69a.75.75 0 0 0 1.5 0v-3.5a.75.75 0 0 0-.75-.75h-3.5a.75.75 0 0 0 0 1.5h1.69L6.22 8.72Z" />
          <path d="M3.5 6.75c0-.69.56-1.25 1.25-1.25H7A.75.75 0 0 0 7 4H4.75A2.75 2.75 0 0 0 2 6.75v4.5A2.75 2.75 0 0 0 4.75 14h4.5A2.75 2.75 0 0 0 12 11.25V9a.75.75 0 0 0-1.5 0v2.25c0 .69-.56 1.25-1.25 1.25h-4.5c-.69 0-1.25-.56-1.25-1.25v-4.5Z" />
          <title>Opens in new window or tab</title>
        </svg>
        `.trim();
        indicator.setAttribute('data-target', 'target-indicator');

        anchor.append(indicator);
      });
  }

  public disconnect(): void {
    this.form
      .querySelectorAll<HTMLAnchorElement>(
        'a[href][data-original-target]:not([href=""])',
      )
      .forEach((anchor) => {
        if (anchor.closest('[contenteditable="true"]')) {
          return;
        }

        const target = anchor.getAttribute('data-original-target');
        anchor.removeAttribute('data-original-target');

        if (target) {
          anchor.setAttribute('target', target);
        } else {
          anchor.removeAttribute('target');
        }

        anchor.querySelector('[data-target="target-indicator"]')?.remove();
      });
  }

  public onSubmit(e: SubmitEvent): void {
    if (!(e.target instanceof HTMLFormElement)) {
      return;
    }

    if (!e.target.checkValidity()) {
      return;
    }

    this.form
      .querySelectorAll<HTMLButtonElement>(
        'button[type="submit"], input[type="submit"]',
      )
      .forEach((value) => {
        value.disabled = true;
      });

    console.log('onSubmit', e);
  }

  public onSubmitEnd(e: Event): void {
    console.log('onSubmitEnd', e);

    this.form
      .querySelectorAll<HTMLButtonElement>(
        'button[type="submit"], input[type="submit"]',
      )
      .forEach((value) => {
        value.disabled = false;
      });
  }

  public onNext(_event: Event): void {
    const nextGroup = this.inputs.__order.find((group) => {
      return this.inputs[group].every((input) =>
        input instanceof HTMLTextAreaElement
          ? !input.value
          : !input.checked &&
            !input.disabled &&
            (input.type === 'checkbox' ||
              input.type === 'radio' ||
              !input.value),
      );
    });

    // Everything is filled in
    if (!nextGroup) {
      const submit = this.form.querySelector<HTMLElement>(
        'input[type="submit"], button[type="submit"]',
      );
      submit?.focus();
      return;
    }

    // Something is not filled in
    const input = this.inputs[nextGroup].find(
      (input) =>
        !input.disabled &&
        (input.type === 'checkbox' || input.type === 'radio' || !input.value),
    )!;

    // Make sure the group is not collapsed
    const collapseGroup = input.closest('[data-controller="collapse"]');
    if (collapseGroup) {
      const trigger = collapseGroup.querySelector<HTMLButtonElement>(
        'button[aria-expanded]',
      );
      if (trigger?.getAttribute('aria-expanded') === 'false') {
        trigger.click();
      }
    }

    input.focus();
  }

  public onToggle(_event: Event): void {
    const firstTrigger = this.form.querySelector(
      '[data-controller="collapse"] button[aria-expanded]',
    );
    if (!firstTrigger) {
      return;
    }

    const currentFocus = document.activeElement;
    const nextValue = firstTrigger.getAttribute('aria-expanded') === 'false';
    const collapsed = this.form.querySelectorAll<HTMLButtonElement>(
      '[data-controller="collapse"] button[aria-expanded]',
    );

    // Click on all buttons that do not match the requested value
    Array.from(collapsed)
      .reverse()
      .forEach((item: HTMLButtonElement) => {
        if (item.getAttribute('aria-expanded') !== String(nextValue)) {
          item.click();
        }
      });

    if (currentFocus && currentFocus instanceof HTMLElement) {
      currentFocus.focus();
    }
  }
}
